import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import {
  fetchValidatorAdminFiles,
  fetchFileAssignmentByFileId,
} from "@store/thunks/validatorAdminStructureThunk";
import { extractFileDetailsFromResponse, normalizeFileData } from "@utils/helpers/dataFormatHelpers";
import { BASE_URL, getStatusStyle, restrictSpecialCharacters } from "@utils/constants";
import { useNavigate } from "react-router-dom";
import { fetchSentenceTableDataValidatorAdmin } from '@store/thunks/validatorAdminThunk';
import { toast } from "react-toastify";
import apiClient from "@/services/api/apiClient";
import { setFileMeta } from "@/store/slices/fileMetaSlice";
import { setFileAndLanguage } from "@/store/slices/fileLanguageSlice";

const FileDetailTableValidatorAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const validatorAdminId = useSelector((state) => state.auth?.user?.userId);
  const token = useSelector((state) => state.auth?.token);

  const [openLangPopupId, setOpenLangPopupId] = useState(null);
  const [assigningLangId, setAssigningLangId] = useState(null);
  const [fileLanguages, setFileLanguages] = useState([]);
  const [folderData, setFolderData] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  const [validatorsByLang, setValidatorsByLang] = useState({});
  const [selectedValidatorForLang, setSelectedValidatorForLang] = useState({});

  const { addAssignedValidators } = useSelector((store) => store.validatorAdmin);
  const assignedValidators = useSelector((state) => state.validatorAdmin.assignedValidators);
  const selectedProjectId = useSelector((state) => state.validatorAdmin.selectedAssignedProjectId);
  const folders = useSelector((state) => state.validatorAdmin.assignedFolders[selectedProjectId] || []);
  const selectedFolderId = useSelector((state) => state.validatorAdmin.selectedAssignedFolderId);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const { userId } = useSelector((store) => store.auth);

  const selectedFolder = folders.find(folder => folder.folderDetailsId === selectedFolderId || folder.folderId === selectedFolderId);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  useEffect(() => {
    if (selectedFolder) {
      fetchFilesForFolder(selectedFolderId);
    }
  }, [selectedFolderId, selectedFolder, reloadTrigger, searchQuery, pageIndex, pageSize]);

  const fetchFilesForFolder = async (folderId) => {
    if (folderId === undefined || folderId === null || folderId === "undefined") {
      console.error("Invalid folder ID provided:", folderId);
      setLoading(false);
      return;
    }

    const folderIdToUse = !isNaN(Number(folderId)) ? Number(folderId) : null;

    if (folderIdToUse === null) {
      console.error("Could not convert folder ID to number:", folderId);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await dispatch(fetchValidatorAdminFiles(folderIdToUse, searchQuery, pageIndex, pageSize));
      const { files, folderData: newFolderData } = extractFileDetailsFromResponse(response);
      if (Array.isArray(response)) {
        if (response.length < pageSize) {
          setHasNextPage(false);
        } else {
          setHasNextPage(true);
        }
      }

      if (newFolderData) {
        setFolderData(prevData => ({
          ...prevData,
          ...newFolderData
        }));
      }

      const normalizedFiles = files.map(normalizeFileData);
      setFileList(normalizedFiles);
    } catch (error) {
      console.error("Error fetching files:", error);
      setFileList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async (fileId) => {
    try {
      setLoadingLanguages(true);
      const result = await dispatch(fetchFileAssignmentByFileId(fileId));
      console.log("[fetchLanguages] Response:", JSON.stringify(result, null, 2));
      setFileLanguages(result);
      setOpenLangPopupId(fileId);
    } catch (err) {
      console.error("[fetchLanguages] Error:", err);
      setFileLanguages([]);
    } finally {
      setLoadingLanguages(false);
    }
  };

  // Fetch languages when popup is opened or reloadTrigger changes
  useEffect(() => {
    if (openLangPopupId) {
      fetchLanguages(openLangPopupId);
    }
  }, [openLangPopupId, reloadTrigger]);

  const fileColumns = [
    {
      header: "Sr. No.",
      cell: ({ row, table }) => {
        const rowIndex = row.index;
        return pageIndex * pageSize + rowIndex + 1;
      },
    },
    {
      header: "File Name",
      accessorFn: (row) => row.file_name || row.fileName || row.filename || "",
      id: "fileName",
      cell: ({ row }) => {
        const fileName = row.original.file_name || row.original.fileName || row.original.filename || "";
        return (
          <span
            className="text-[#ec7026] hover:text-[#d85d1a] hover:underline cursor-pointer"
            onClick={() => fetchLanguages(row.original.fileId)}
          >
            {fileName}
          </span>
        );
      },
    },
    {
      header: "Total Sentences",
      id: "totalSentence",
      cell: ({ row }) => {
        return <span>{row.original.totalSentences}</span>;
      },
    },
    {
      header: "Size",
      accessorFn: (row) => row.fileSizeBytes,
      id: "fileSize",
      cell: ({ row }) => {
        const fileSize = row.original.fileSizeBytes;
        return <span>{formatFileSize(fileSize)}</span>;
      },
    },
    {
      header: "Status",
      accessorFn: (row) => row.status || row.fileStatus || "Pending",
      id: "status",
      cell: ({ row }) => {
        const status = row.original.status || row.original.fileStatus || "Pending";
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusStyle(row.original.status).bg} ${getStatusStyle(row.original.status).text}`}>
            {status}
          </span>
        );
      },
    },
    {
      header: "Assigned Date",
      accessorFn: (row) => row.uploaded_at || row.uploadedAt || row.assignedAt || "",
      id: "assignedDate",
      cell: ({ row }) => {
        const deadline = row.original?.uploadedAt || row.uploadedAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
      cellClassName: "text-center",
    },
  ];

  const formatFileSize = (size) => {
    if (!size) return "0 B";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Fetch validators for all languages when fileLanguages changes
  useEffect(() => {
    if (fileLanguages && fileLanguages.length > 0) {
      fileLanguages.forEach(async (lang) => {
        const targetId = lang.targetId || lang.langId;
        if (!validatorsByLang[targetId]) {
          try {
            const result = await apiClient(`${BASE_URL}api/validatorAdmin/getVU?languageId=${targetId}&validatorAdminId=${userId}`, {
              method: "GET",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
            }, navigate);

            setValidatorsByLang((prev) => ({
              ...prev,
              [targetId]: result.status === "success" ? result.data : []
            }));
          } catch (error) {
            console.error("Error fetching validators for languageId", targetId, error);
            setValidatorsByLang((prev) => ({
              ...prev,
              [targetId]: []
            }));
          }
        }
      });
    }
  }, [fileLanguages]);

  // Assign validator to language
  const handleAssignValidator = async (lang, validatorId) => {
    const body = {
      fileId: openLangPopupId,
      sourceLanguageId: lang.sourceId || lang.sourceLanguageId,
      targetLanguageId: lang.targetId || lang.langId,
      validatorUserId: validatorId,
    };

    try {
      console.log("[handleAssignValidator] Assigning validator:", body);
      const result = await apiClient(
        `${BASE_URL}api/validatorAdmin/assignVU?validatorAdminId=${userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(body),
        }
        , navigate
      );
      if (result.status === "success") {
        toast.success(
          <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
            Validator assigned successfully!
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            className: "rounded-lg shadow-md border border-green-200",
          }
        );
        // Reset validator selection and trigger reload
        setSelectedValidatorForLang((prev) => ({
          ...prev,
          [lang.targetId || lang.langId]: "",
        }));
        setReloadTrigger((prev) => prev + 1);
      } else {
        toast.error(
          <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
            Error: {result.message || "Failed to assign validator"}
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            className: "rounded-lg shadow-md border border-red-200",
          }
        );
      }
    } catch (error) {
      console.error("[handleAssignValidator] Error assigning validator:", error);
      toast.error(
        <div className="p-3 font-serif text-sm font-medium text-white">
          Error: Failed to assign validator
        </div>,
        {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
          className: "rounded-lg shadow-md border border-red-200",
        }
      );
    }
  };

  // Send to GSAdmin
  const handleSendToGSAdmin = async (lang) => {
    const body = {
      fileId: openLangPopupId,
      sourceLanguageId: lang.sourceId || lang.sourceLanguageId,
      targetLanguageId: lang.targetId || lang.langId,
    };
    try {
      console.log("[handleSendToGSAdmin] Sending to GSAdmin:", body);
      const result = await apiClient(
        `${BASE_URL}api/validatorAdmin/sendToGS`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(body),
        }, navigate
      );
      if (result.status === "success") {
        toast.success(
          <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
            Sent to GSAdmin successfully!
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            className: "rounded-lg shadow-md border border-green-200",
          }
        );
        // Trigger reload to refresh popup
        setReloadTrigger((prev) => prev + 1);
      } else {
        toast.error(
          <div className="p-3 font-serif text-sm font-medium text-white">
            Error: {result.message || "Failed to send to GSAdmin"}
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            className: "rounded-lg shadow-md border border-red-200",
          }
        );
      }
    } catch (error) {
      console.error("[handleSendToGSAdmin] Error sending to GSAdmin:", error);
      toast.error(
        <div className="p-3 font-serif text-sm font-medium text-[#d85d1a]">
          Error: Failed to send to GSAdmin
        </div>,
        {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
          className: "rounded-lg shadow-md border border-red-200",
        }
      );
    }
  };

  if (!selectedFolderId) {
    return (
      <div className="p-4">
        <h2 className="text-xl font-bold mb-4">No Folder Selected</h2>
        <p>Please select a folder first to view its files.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <h2 className="text-xl font-bold mb-4">Loading files...</h2>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4 w-1/2"></div>
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="validator-admin-table p-4">
        {!loading && (!fileList || fileList.length === 0) ? (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
            <p className="text-yellow-700">No files found in this folder.</p>
          </div>
        ) : null}

        <GenericTable
          data={[...fileList || []].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
          manualPagination={true}
          pageIndex={pageIndex}
          pageSize={pageSize}
          hasNextPage={hasNextPage}
          onPaginationChange={({ pageIndex, pageSize }) => {
            setPageIndex(pageIndex);
            setPageSize(pageSize);
          }}
          columns={fileColumns}
          showPagination={true}
          emptyMessage="No files available in this folder"
        />

        {openLangPopupId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-200">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-[90vw] max-w-7xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
                <p className="text-sm text-gray-500 font-['Semantic/Strong'] mt-1">
                  Details of languages assigned for the selected file
                </p>
              </div>
              <div className="p-4 sm:p-6 lg:p-8">
                {loadingLanguages ? (
                  <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  </div>
                ) : Array.isArray(fileLanguages) && fileLanguages.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr className="text-gray-900 text-sm font-['Semantic/Strong']">
                          <th className="border border-gray-200 px-4 py-2 text-left">Source Language</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Target Language</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Validator Name</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Sentence Statistics</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Select Validator User</th>
                          <th className="border border-gray-200 px-4 py-2 text-center">Assign Validator</th>
                          <th className="border border-gray-200 px-4 py-2 text-center">Open File</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700 text-sm font-['Semantic/Strong']">
                        {fileLanguages.map((lang, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="border border-gray-200 px-4 py-2">{lang.sourceName || "N/A"}</td>
                            <td className="border border-gray-200 px-4 py-2">{lang.targetName || "N/A"}</td>
                            <td className="border border-gray-200 px-4 py-2">
                              <span className={`px-2 py-1 rounded-full ${getStatusStyle(lang.vaStatus).bg} ${getStatusStyle(lang.vaStatus).text}`}>
                                {lang.vaStatus}
                              </span>
                            </td>
                            <td className="border border-gray-200 px-4 py-2">{lang.validationUserName || "N/A"}</td>
                            <td className="border border-gray-200 px-4 py-2">
                              <div className="text-xs">
                                <div className="flex justify-between mb-1">
                                  <span className="text-gray-600">Total:</span>
                                  <span className="font-medium">{lang.totalSentences}</span>
                                </div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-green-600">Translated:</span>
                                  <span className="font-medium text-green-600">{lang.validatedSentenceCount}</span>
                                </div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-red-600">Rejected:</span>
                                  <span className="font-medium text-red-600">{lang.rejectedSentenceCount}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-blue-600">Completed:</span>
                                  <span className="font-medium text-blue-600">{lang.percentageCompleted}%</span>
                                </div>
                              </div>
                            </td>
                            <td className="border border-gray-200 px-4 py-2">
                              {lang.vaStatus !== 'ASSIGNED' && lang.vaStatus !== 'COMPLETED' && lang.vaStatus !== 'SUBMITTED' && lang.vaStatus !== 'SUBMITTED_TO_CDAC' ? (
                                <select
                                  className="border rounded px-2 py-1"
                                  value={selectedValidatorForLang[lang.targetId] || ""}
                                  onChange={(e) =>
                                    setSelectedValidatorForLang({
                                      [lang.targetId]: e.target.value,
                                    })
                                  }
                                >
                                  <option value="" disabled>Select Validator</option>
                                  {validatorsByLang[lang.targetId] && validatorsByLang[lang.targetId].length > 0 ? (
                                    validatorsByLang[lang.targetId].map((val) => (
                                      <option key={val.userId} value={val.userId}>{val.userName}</option>
                                    ))
                                  ) : (
                                    <option disabled>No validators found</option>
                                  )}
                                </select>
                              ) : null}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-center">
                              {lang.vaStatus === "COMPLETED" ? (
                                <button
                                  className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 focus:outline-none transition-colors duration-150"
                                  onClick={() => handleSendToGSAdmin(lang)}
                                >
                                  Send to GSAdmin
                                </button>
                              ) : lang.vaStatus === 'ASSIGNED' ? (
                                <span className="text-blue-600 font-semibold">Assigned to Validator</span>
                              ) : (lang.vaStatus === 'SUBMITTED_TO_GS' || lang.vaStatus === 'SUBMITTED') ? (
                                <span className="text-green-600 font-semibold">Sent to GS</span>
                              ) : (
                                <button
                                  className={`px-4 py-2 rounded bg-blue-500 text-white font-semibold hover:bg-blue-600 focus:outline-none transition-colors duration-150 ${!selectedValidatorForLang[lang.targetId] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  disabled={!selectedValidatorForLang[lang.targetId]}
                                  onClick={() => handleAssignValidator(lang, selectedValidatorForLang[lang.targetId])}
                                >
                                  Assign
                                </button>
                              )}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-center">
                              <button
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md"
                                onClick={async () => {
                                  console.log('validatorAdminId:', validatorAdminId);
                                  dispatch(setFileMeta({
                                    fileName: lang.fileName
                                  }));
                                  dispatch(setFileAndLanguage({
                                    targetLanguageName: lang.targetName,
                                  }))
                                  await dispatch(fetchSentenceTableDataValidatorAdmin({
                                    fileId: openLangPopupId,
                                    sourceId: lang.sourceId,
                                    targetId: lang.targetId,
                                    validatorAdminId: localStorage.getItem("userId"),
                                  }));
                                  navigate(`/validatorAdmin/folders/files/sentences/${openLangPopupId}/${lang.sourceId}/${lang.targetId}`);
                                }}
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 font-['Semantic/Strong']">No languages assigned</p>
                )}
                <div className="px-4 sm:px-6 lg:px-8 py-4 text-right">
                  <button
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                    onClick={() => setOpenLangPopupId(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailTableValidatorAdmin;